import { useState, useEffect } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { 
  Users, 
  Plus,
  Pencil,
  Trash,
  Shield,
  Key,
  UserCircle
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Permission categories for grouping
const PERMISSION_CATEGORIES = {
  "Dashboard": ["dashboard.view"],
  "Orders": ["orders.view", "orders.manage"],
  "Returns": ["returns.view", "returns.manage"],
  "Products": ["products.view", "products.create", "products.edit", "products.delete", "products.qc"],
  "Inventory": ["inventory.view", "inventory.manage"],
  "Warehouses": ["warehouses.view", "warehouses.manage"],
  "Reconciliation": ["reconciliation.view", "reconciliation.claims"],
  "Reports": ["reports.view"],
  "Users": ["users.view", "users.manage"],
  "Settings": ["settings.view", "settings.manage"],
  "KYC": ["kyc.view", "kyc.submit"],
  "Roles": ["roles.view", "roles.manage"]
};

export default function UsersPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'staff',
    permissions: []
  });
  
  const [roleFormData, setRoleFormData] = useState({
    name: '',
    description: '',
    permissions: []
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, rolesRes, permsRes] = await Promise.all([
        axios.get(`${API_URL}/api/users`, { withCredentials: true }),
        axios.get(`${API_URL}/api/rbac/roles`, { withCredentials: true }).catch(() => ({ data: { roles: [] } })),
        axios.get(`${API_URL}/api/rbac/permissions`, { withCredentials: true }).catch(() => ({ data: { permissions: {} } }))
      ]);
      
      setUsers(usersRes.data);
      setRoles(rolesRes.data.roles || []);
      setAllPermissions(permsRes.data.permissions || {});
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { ...userFormData };
        if (!updateData.password) delete updateData.password;
        await axios.patch(`${API_URL}/api/users/${editingUser.user_id}`, updateData, { withCredentials: true });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API_URL}/api/users`, userFormData, { withCredentials: true });
        toast.success('User created successfully');
      }
      
      fetchData();
      handleCloseUserDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingRole) {
        await axios.patch(`${API_URL}/api/rbac/roles/${editingRole.role_id}`, roleFormData, { withCredentials: true });
        toast.success('Role updated successfully');
      } else {
        await axios.post(`${API_URL}/api/rbac/roles`, roleFormData, { withCredentials: true });
        toast.success('Role created successfully');
      }
      
      fetchData();
      handleCloseRoleDialog();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/users/${userId}`, { withCredentials: true });
      toast.success('User deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/rbac/roles/${roleId}`, { withCredentials: true });
      toast.success('Role deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete role');
    }
  };

  const handleCloseUserDialog = () => {
    setUserDialogOpen(false);
    setEditingUser(null);
    setUserFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      role: 'staff',
      permissions: []
    });
  };

  const handleCloseRoleDialog = () => {
    setRoleDialogOpen(false);
    setEditingRole(null);
    setRoleFormData({
      name: '',
      description: '',
      permissions: []
    });
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      password: '',
      role: user.role,
      permissions: user.permissions || []
    });
    setUserDialogOpen(true);
  };

  const handleEditRole = (role) => {
    if (role.is_system) {
      toast.error('System roles cannot be edited');
      return;
    }
    setEditingRole(role);
    setRoleFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || []
    });
    setRoleDialogOpen(true);
  };

  const togglePermission = (permission, isRole = false) => {
    if (isRole) {
      const current = roleFormData.permissions;
      if (current.includes(permission)) {
        setRoleFormData({ ...roleFormData, permissions: current.filter(p => p !== permission) });
      } else {
        setRoleFormData({ ...roleFormData, permissions: [...current, permission] });
      }
    } else {
      const current = userFormData.permissions;
      if (current.includes(permission)) {
        setUserFormData({ ...userFormData, permissions: current.filter(p => p !== permission) });
      } else {
        setUserFormData({ ...userFormData, permissions: [...current, permission] });
      }
    }
  };

  const toggleCategory = (category, isRole = false) => {
    const categoryPerms = PERMISSION_CATEGORIES[category];
    const formData = isRole ? roleFormData : userFormData;
    const setFormData = isRole ? setRoleFormData : setUserFormData;
    const current = formData.permissions;
    
    const allSelected = categoryPerms.every(p => current.includes(p));
    
    if (allSelected) {
      setFormData({ ...formData, permissions: current.filter(p => !categoryPerms.includes(p)) });
    } else {
      const newPerms = [...current];
      categoryPerms.forEach(p => {
        if (!newPerms.includes(p)) newPerms.push(p);
      });
      setFormData({ ...formData, permissions: newPerms });
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getRoleLabel = (roleId) => {
    const role = roles.find(r => r.role_id === roleId);
    return role?.name || roleId;
  };

  return (
    <DashboardLayout 
      title="Access Management" 
      subtitle="Manage users, roles and permissions"
    >
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="bg-white border border-[#e5e5ea] p-1">
          <TabsTrigger value="users" className="gap-2" data-testid="users-tab">
            <Users size={16} /> Users
          </TabsTrigger>
          <TabsTrigger value="roles" className="gap-2" data-testid="roles-tab">
            <Shield size={16} /> Roles & Permissions
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">
                {users.length} User{users.length !== 1 ? 's' : ''}
              </h3>
              <Button 
                onClick={() => setUserDialogOpen(true)}
                className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                data-testid="add-user-btn"
              >
                <Plus size={18} className="mr-2" />
                Add User
              </Button>
            </div>
            
            {loading ? (
              <div className="loading-spinner">
                <div className="spinner"></div>
              </div>
            ) : users.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full sp-table">
                  <thead>
                    <tr>
                      <th className="text-left p-4">User</th>
                      <th className="text-left p-4">Email</th>
                      <th className="text-left p-4">Phone</th>
                      <th className="text-left p-4">Role</th>
                      <th className="text-left p-4">Status</th>
                      <th className="text-left p-4">Created</th>
                      <th className="text-left p-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} className="hover:bg-[#fcfcfc]">
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded bg-[#7d64ff] flex items-center justify-center text-white text-sm font-bold">
                              {user.name?.charAt(0)?.toUpperCase()}
                            </div>
                            <span className="font-medium">{user.name}</span>
                            {user.user_id === currentUser?.user_id && (
                              <span className="text-xs px-2 py-0.5 bg-[#7d64ff]/10 text-[#7d64ff] rounded">You</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-[#5a5c87]">{user.email}</td>
                        <td className="p-4 text-[#5a5c87]">{user.phone || '-'}</td>
                        <td className="p-4">
                          <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold bg-[#fcfcfc] rounded">
                            <Shield size={12} />
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td className="p-4">
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded ${user.is_active ? 'sp-badge-success' : 'sp-badge-danger'}`}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-4 text-sm text-[#5a5c87]">{formatDate(user.created_at)}</td>
                        <td className="p-4">
                          {user.user_id !== currentUser?.user_id && (
                            <div className="flex gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditUser(user)}
                                data-testid={`edit-user-${user.user_id}`}
                              >
                                <Pencil size={16} />
                              </Button>
                              {user.parent_seller_id && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-[#ea4b5a] hover:text-[#c43447] hover:bg-[#ea4b5a]/10"
                                  onClick={() => handleDeleteUser(user.user_id)}
                                  data-testid={`delete-user-${user.user_id}`}
                                >
                                  <Trash size={16} />
                                </Button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <Users className="empty-state-icon" />
                <h4 className="empty-state-title">No team members</h4>
                <p className="empty-state-description">Add team members to help manage your store</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Roles Tab */}
        <TabsContent value="roles">
          <div className="data-table-container">
            <div className="data-table-header">
              <h3 className="data-table-title">
                {roles.length} Role{roles.length !== 1 ? 's' : ''}
              </h3>
              <Button 
                onClick={() => setRoleDialogOpen(true)}
                className="bg-[#7d64ff] hover:bg-[#6153a7] text-white"
                data-testid="add-role-btn"
              >
                <Plus size={18} className="mr-2" />
                Create Custom Role
              </Button>
            </div>
            
            <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {roles.map((role) => (
                <div 
                  key={role.role_id}
                  className={`bg-white border p-4 sp-card-hover ${role.is_system ? 'border-[#e5e5ea]' : 'border-[#7d64ff]/30'}`}
                  data-testid={`role-card-${role.role_id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-10 h-10 flex items-center justify-center rounded ${role.is_system ? 'bg-[#fcfcfc]' : 'bg-[#7d64ff]/10'}`}>
                        <Shield size={20} className={role.is_system ? 'text-[#5a5c87]' : 'text-[#7d64ff]'} weight="bold" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#0A0A0A]">{role.name}</h4>
                        {role.is_system && (
                          <span className="text-xs text-[#5a5c87]">System Role</span>
                        )}
                      </div>
                    </div>
                    {!role.is_system && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditRole(role)}>
                          <Pencil size={14} />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-[#ea4b5a]"
                          onClick={() => handleDeleteRole(role.role_id)}
                        >
                          <Trash size={14} />
                        </Button>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-[#5a5c87] mb-3">{role.description}</p>
                  
                  <div className="flex items-center gap-2 text-xs text-[#5a5c87]">
                    <Key size={14} />
                    <span>{role.permissions?.length || 0} permissions</span>
                  </div>
                  
                  {role.permissions && role.permissions.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-[#e5e5ea]">
                      <div className="flex flex-wrap gap-1">
                        {role.permissions.slice(0, 5).map(perm => (
                          <span key={perm} className="text-xs px-1.5 py-0.5 bg-[#fcfcfc] rounded">
                            {perm.split('.')[0]}
                          </span>
                        ))}
                        {role.permissions.length > 5 && (
                          <span className="text-xs px-1.5 py-0.5 bg-[#fcfcfc] rounded">
                            +{role.permissions.length - 5} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit User Dialog */}
      <Dialog open={userDialogOpen} onOpenChange={handleCloseUserDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold flex items-center gap-2">
              <UserCircle size={24} />
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUserSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Full Name *</Label>
                <Input
                  value={userFormData.name}
                  onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                  required
                  className="mt-1 rounded-sm"
                  data-testid="user-name"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Email *</Label>
                <Input
                  type="email"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  required
                  disabled={!!editingUser}
                  className="mt-1 rounded-sm"
                  data-testid="user-email"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Phone</Label>
                <Input
                  type="tel"
                  value={userFormData.phone}
                  onChange={(e) => setUserFormData({ ...userFormData, phone: e.target.value })}
                  className="mt-1 rounded-sm"
                  data-testid="user-phone"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">
                  {editingUser ? 'New Password' : 'Password *'}
                </Label>
                <Input
                  type="password"
                  value={userFormData.password}
                  onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
                  required={!editingUser}
                  placeholder={editingUser ? 'Leave blank to keep current' : 'Min. 6 characters'}
                  className="mt-1 rounded-sm"
                  data-testid="user-password"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Role *</Label>
              <Select value={userFormData.role} onValueChange={(value) => setUserFormData({ ...userFormData, role: value })}>
                <SelectTrigger className="mt-1 rounded-sm" data-testid="user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {roles.map(role => (
                    <SelectItem key={role.role_id} value={role.role_id}>
                      <div className="flex items-center gap-2">
                        <Shield size={14} />
                        <span>{role.name}</span>
                        {role.is_system && <span className="text-xs text-[#5a5c87]">(System)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2 block">
                Custom Permissions (Override Role)
              </Label>
              <div className="border border-[#e5e5ea] rounded p-3 max-h-48 overflow-y-auto">
                {Object.entries(PERMISSION_CATEGORIES).map(([category, perms]) => (
                  <div key={category} className="mb-3 last:mb-0">
                    <label className="flex items-center gap-2 mb-1 cursor-pointer">
                      <Checkbox
                        checked={perms.every(p => userFormData.permissions.includes(p))}
                        onCheckedChange={() => toggleCategory(category, false)}
                      />
                      <span className="text-sm font-semibold">{category}</span>
                    </label>
                    <div className="ml-6 flex flex-wrap gap-2">
                      {perms.map(perm => (
                        <label key={perm} className="flex items-center gap-1 cursor-pointer text-xs text-[#5a5c87]">
                          <Checkbox
                            checked={userFormData.permissions.includes(perm)}
                            onCheckedChange={() => togglePermission(perm, false)}
                          />
                          {perm.split('.')[1]}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-[#5a5c87] mt-1">Leave empty to use role's default permissions</p>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseUserDialog} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" data-testid="save-user-btn">
                {editingUser ? 'Update User' : 'Add User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Role Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={handleCloseRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-bold flex items-center gap-2">
              <Shield size={24} />
              {editingRole ? 'Edit Role' : 'Create Custom Role'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRoleSubmit} className="space-y-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Role Name *</Label>
              <Input
                value={roleFormData.name}
                onChange={(e) => setRoleFormData({ ...roleFormData, name: e.target.value })}
                required
                placeholder="e.g., Warehouse Manager"
                className="mt-1 rounded-sm"
                data-testid="role-name"
              />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87]">Description</Label>
              <Input
                value={roleFormData.description}
                onChange={(e) => setRoleFormData({ ...roleFormData, description: e.target.value })}
                placeholder="Brief description of this role"
                className="mt-1 rounded-sm"
                data-testid="role-description"
              />
            </div>

            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5a5c87] mb-2 block">
                Permissions *
              </Label>
              <div className="border border-[#e5e5ea] rounded p-3 max-h-64 overflow-y-auto">
                {Object.entries(PERMISSION_CATEGORIES).map(([category, perms]) => (
                  <div key={category} className="mb-4 last:mb-0">
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                      <Checkbox
                        checked={perms.every(p => roleFormData.permissions.includes(p))}
                        onCheckedChange={() => toggleCategory(category, true)}
                      />
                      <span className="text-sm font-bold">{category}</span>
                      <span className="text-xs text-[#5a5c87]">({perms.length} permissions)</span>
                    </label>
                    <div className="ml-6 grid grid-cols-2 gap-2">
                      {perms.map(perm => (
                        <label key={perm} className="flex items-center gap-2 cursor-pointer">
                          <Checkbox
                            checked={roleFormData.permissions.includes(perm)}
                            onCheckedChange={() => togglePermission(perm, true)}
                          />
                          <span className="text-sm">
                            <span className="text-[#0A0A0A]">{perm.split('.')[1]}</span>
                            <span className="text-xs text-[#5a5c87] ml-1">
                              ({allPermissions[perm]?.split(' ').slice(0, 3).join(' ') || perm})
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseRoleDialog} className="flex-1">
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={roleFormData.permissions.length === 0}
                className="flex-1 bg-[#7d64ff] hover:bg-[#6153a7] text-white" 
                data-testid="save-role-btn"
              >
                {editingRole ? 'Update Role' : 'Create Role'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
